/**
 * Canonical error codes shared by API, dashboard, SDK and CLI.
 * The API returns these verbatim in the error envelope so clients can branch on
 * `error.code` instead of parsing messages.
 */
export const ErrorCode = {
  // auth / session
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  AUTH_REFRESH_REUSE: 'AUTH_REFRESH_REUSE',
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_EMAIL_NOT_VERIFIED: 'AUTH_EMAIL_NOT_VERIFIED',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  CSRF_ORIGIN_REJECTED: 'CSRF_ORIGIN_REJECTED',

  // tenancy / rbac
  ORG_ACCESS_DENIED: 'ORG_ACCESS_DENIED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  PLATFORM_ROLE_REQUIRED: 'PLATFORM_ROLE_REQUIRED',
  INVITATION_INVALID: 'INVITATION_INVALID',
  OWNERSHIP_TRANSFER_INVALID: 'OWNERSHIP_TRANSFER_INVALID',

  // generic
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',

  // cms
  CONTENT_KEY_INVALID: 'CONTENT_KEY_INVALID',
  CONTENT_KEY_DUPLICATE: 'CONTENT_KEY_DUPLICATE',
  CONTENT_VALUE_INVALID: 'CONTENT_VALUE_INVALID',
  CONTENT_VERSION_NOT_FOUND: 'CONTENT_VERSION_NOT_FOUND',
  PUBLISH_IN_PROGRESS: 'PUBLISH_IN_PROGRESS',
  PUBLISH_BLOCKED_BY_LICENCE: 'PUBLISH_BLOCKED_BY_LICENCE',
  SITE_SLUG_TAKEN: 'SITE_SLUG_TAKEN',
  PLAN_LIMIT_REACHED: 'PLAN_LIMIT_REACHED',
  MEDIA_INVALID_FILE: 'MEDIA_INVALID_FILE',
  MEDIA_TOO_LARGE: 'MEDIA_TOO_LARGE',
  PREVIEW_TOKEN_INVALID: 'PREVIEW_TOKEN_INVALID',

  // licensing
  LICENCE_NOT_FOUND: 'LICENCE_NOT_FOUND',
  LICENCE_KEY_INVALID: 'LICENCE_KEY_INVALID',
  LICENCE_SLOTS_EXHAUSTED: 'LICENCE_SLOTS_EXHAUSTED',
  LICENCE_EXPIRED: 'LICENCE_EXPIRED',
  LICENCE_SUSPENDED: 'LICENCE_SUSPENDED',
  LICENCE_REVOKED: 'LICENCE_REVOKED',
  LICENCE_CANCELLED: 'LICENCE_CANCELLED',
  LICENCE_DOMAIN_NOT_ALLOWED: 'LICENCE_DOMAIN_NOT_ALLOWED',
  LICENCE_DOMAIN_UNVERIFIED: 'LICENCE_DOMAIN_UNVERIFIED',
  LICENCE_IP_NOT_ALLOWED: 'LICENCE_IP_NOT_ALLOWED',
  LICENCE_FINGERPRINT_MISMATCH: 'LICENCE_FINGERPRINT_MISMATCH',
  LICENCE_FINGERPRINT_REQUIRED: 'LICENCE_FINGERPRINT_REQUIRED',
  LICENCE_ENV_NOT_ALLOWED: 'LICENCE_ENV_NOT_ALLOWED',
  LICENCE_ALREADY_ACTIVE: 'LICENCE_ALREADY_ACTIVE',
  LICENCE_ACTIVATION_NOT_FOUND: 'LICENCE_ACTIVATION_NOT_FOUND',
  LICENCE_DEACTIVATION_BLOCKED: 'LICENCE_DEACTIVATION_BLOCKED',
  LICENCE_REPLAY_DETECTED: 'LICENCE_REPLAY_DETECTED',
  LICENCE_STATE_TRANSITION_INVALID: 'LICENCE_STATE_TRANSITION_INVALID',
  LICENCE_FILE_INVALID_SIGNATURE: 'LICENCE_FILE_INVALID_SIGNATURE',
  LICENCE_FILE_UNKNOWN_KID: 'LICENCE_FILE_UNKNOWN_KID',
  LICENCE_FILE_MALFORMED: 'LICENCE_FILE_MALFORMED',
  LICENCE_FILES_NOT_ENABLED: 'LICENCE_FILES_NOT_ENABLED',
  LICENCE_DEGRADED: 'LICENCE_DEGRADED',

  // billing
  BILLING_NO_SUBSCRIPTION: 'BILLING_NO_SUBSCRIPTION',
  BILLING_PROVIDER_ERROR: 'BILLING_PROVIDER_ERROR',
  BILLING_WEBHOOK_INVALID: 'BILLING_WEBHOOK_INVALID',
  BILLING_PRICE_NOT_FOUND: 'BILLING_PRICE_NOT_FOUND',
  BILLING_COUPON_INVALID: 'BILLING_COUPON_INVALID',

  // ops / security
  WEBHOOK_URL_BLOCKED: 'WEBHOOK_URL_BLOCKED',
  SSRF_BLOCKED: 'SSRF_BLOCKED',
  DOMAIN_VERIFICATION_FAILED: 'DOMAIN_VERIFICATION_FAILED',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export interface AppErrorOptions {
  status?: number;
  details?: unknown;
  hint?: string;
  cause?: unknown;
}

/** Default HTTP status per code; overridable per throw site. */
const DEFAULT_STATUS: Partial<Record<ErrorCode, number>> = {
  AUTH_INVALID_CREDENTIALS: 401,
  AUTH_TOKEN_EXPIRED: 401,
  AUTH_TOKEN_INVALID: 401,
  AUTH_REFRESH_REUSE: 401,
  AUTH_ACCOUNT_LOCKED: 423,
  AUTH_EMAIL_NOT_VERIFIED: 403,
  AUTH_REQUIRED: 401,
  CSRF_ORIGIN_REJECTED: 403,
  ORG_ACCESS_DENIED: 403,
  PERMISSION_DENIED: 403,
  PLATFORM_ROLE_REQUIRED: 403,
  INVITATION_INVALID: 400,
  OWNERSHIP_TRANSFER_INVALID: 400,
  VALIDATION_FAILED: 422,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  CONTENT_KEY_INVALID: 422,
  CONTENT_KEY_DUPLICATE: 409,
  CONTENT_VALUE_INVALID: 422,
  CONTENT_VERSION_NOT_FOUND: 404,
  PUBLISH_IN_PROGRESS: 409,
  PUBLISH_BLOCKED_BY_LICENCE: 402,
  SITE_SLUG_TAKEN: 409,
  PLAN_LIMIT_REACHED: 402,
  MEDIA_INVALID_FILE: 415,
  MEDIA_TOO_LARGE: 413,
  PREVIEW_TOKEN_INVALID: 401,
  LICENCE_NOT_FOUND: 404,
  LICENCE_KEY_INVALID: 400,
  LICENCE_SLOTS_EXHAUSTED: 409,
  LICENCE_EXPIRED: 402,
  LICENCE_SUSPENDED: 402,
  LICENCE_REVOKED: 402,
  LICENCE_CANCELLED: 402,
  LICENCE_DOMAIN_NOT_ALLOWED: 403,
  LICENCE_DOMAIN_UNVERIFIED: 202,
  LICENCE_IP_NOT_ALLOWED: 403,
  LICENCE_FINGERPRINT_MISMATCH: 403,
  LICENCE_FINGERPRINT_REQUIRED: 400,
  LICENCE_ENV_NOT_ALLOWED: 403,
  LICENCE_ALREADY_ACTIVE: 409,
  LICENCE_ACTIVATION_NOT_FOUND: 404,
  LICENCE_DEACTIVATION_BLOCKED: 409,
  LICENCE_REPLAY_DETECTED: 409,
  LICENCE_STATE_TRANSITION_INVALID: 409,
  LICENCE_FILE_INVALID_SIGNATURE: 400,
  LICENCE_FILE_UNKNOWN_KID: 400,
  LICENCE_FILE_MALFORMED: 400,
  LICENCE_FILES_NOT_ENABLED: 403,
  LICENCE_DEGRADED: 402,
  BILLING_NO_SUBSCRIPTION: 409,
  BILLING_PROVIDER_ERROR: 502,
  BILLING_WEBHOOK_INVALID: 400,
  BILLING_PRICE_NOT_FOUND: 404,
  BILLING_COUPON_INVALID: 400,
  WEBHOOK_URL_BLOCKED: 400,
  SSRF_BLOCKED: 400,
  DOMAIN_VERIFICATION_FAILED: 400,
};

/** Isomorphic application error carrying a stable code + HTTP status. */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;
  readonly hint?: string;

  constructor(code: ErrorCode, message?: string, options: AppErrorOptions = {}) {
    super(message ?? code, options.cause ? { cause: options.cause } : undefined);
    this.name = 'AppError';
    this.code = code;
    this.status = options.status ?? DEFAULT_STATUS[code] ?? 400;
    this.details = options.details;
    this.hint = options.hint;
  }

  static isAppError(value: unknown): value is AppError {
    return value instanceof AppError;
  }

  toEnvelope(requestId?: string) {
    return {
      error: {
        code: this.code,
        message: this.message,
        status: this.status,
        ...(this.details !== undefined ? { details: this.details } : {}),
        ...(this.hint ? { hint: this.hint } : {}),
        ...(requestId ? { requestId } : {}),
      },
    };
  }
}

export const defaultStatusFor = (code: ErrorCode): number => DEFAULT_STATUS[code] ?? 400;
